import React from 'react';
import { red } from '../../../theme/themePrimitives';
import { Box, InputLabel, MenuItem, Select, SelectChangeEvent, SxProps } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';

/**
 * @heading1: CustomDropdown Component
 * @block_quotes: A reusable dropdown component with styling and error handling.
 * @heading3: Interfaces
 * @heading4: Option Interface
 * @ol:
 * label : {string} - The display text for the dropdown option.
 * value : {string | number} - The value of the dropdown option.
 */
interface Option {
  label: string;
  value: string | number | null;
  icon?: React.ReactNode;
  disabled?: boolean;
}

/**
 * @heading4: DropDown Interface
 * @ol:
 * label : {string} - Label displayed above the dropdown.
 * options : {Option[]} - Array of options for the dropdown.
 * value : {string | number} - The current selected value.
 * onChange : {(event: SelectChangeEvent<unknown>) => void} - Function triggered when the value changes.
 * error=false : {boolean} - Flag to indicate an error state.
 * @separator:
 */
interface DropDownInterface {
  label?: string;
  placeholder?: string;
  options: Option[];
  value: string | number | null;
  onChange: (event: SelectChangeEvent<unknown>) => void;
  error?: boolean;
  enabledDefaultSelection?: boolean;
  sx?: SxProps<Theme> | undefined;
  [key: string]: unknown;
}

/**
 * @heading3: Styled Components
 * @heading4: Styled Form Control
 * @block_quotes: Setting Form Control width to 100%, giving margin bottom to 16px(1rem) and box sizing to border box
 */

const StyledFormControl = styled(Box)`
  width: 100%;
  // margin-bottom: 1rem;
  // box-sizing: border-box;
  // max-width: 100%;
`;

/**
 * @heading4: Styled Select
 * @block_quotes: Select Dropdown styles have been applied here
 * @separator:
 */
const StyledSelect = styled(Select)`
  box-sizing: border-box;
  & .MuiSelect-select {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    // display: flex;
    // align-items: center;
    // max-width: calc(100% - 15px);
    // margin-right: 15px;
  }
`;
// const StyledSelect = styled(Select)<{ error?: boolean }>(({ theme, error }) => ({
//   'boxSizing': 'border-box',
//   'width': '100%',
//   '& .MuiSelect-select': {
//     whiteSpace: 'nowrap',
//     overflow: 'hidden',
//     textOverflow: 'ellipsis',
//     width: '100%'
//   },
//   '& .MuiOutlinedInput-notchedOutline': {
//     borderColor: error ? red[300] : 'transparent'
//   },
//   '&:hover .MuiOutlinedInput-notchedOutline': {
//     borderColor: error ? red[300] : theme.palette.primary.main
//   }
// }));

/**
 * @heading2: CustomDropdown Component
 * @block_quotes: A reusable dropdown field component.
 * @heading4: Parameter
 * @ol:
 * Reference of Dropdown interface {DropDownInterface}
 * props : {CustomInputProps} - Props for configuring the input field.
 */

const CustomDropdown: React.FC<DropDownInterface> = ({
  label,
  placeholder = 'Select an option',
  options,
  value,
  onChange,
  error = false,
  enabledDefaultSelection = false,
  sx,
  ...props
}) => {
  const formattedValue = value ? value : 'choose';
  return (
    <StyledFormControl sx={sx}>
      {label && <InputLabel>{label}</InputLabel>}
      <StyledSelect
        value={formattedValue}
        onChange={onChange}
        error={error}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
              overflowY: 'auto'
            }
          }
        }}
        sx={{
          '& .Mui-error': {
            border: `1px solid ${red[300]} !important`
          }
        }}
        {...props}
      >
        <MenuItem value="choose" disabled={!enabledDefaultSelection}>
          {placeholder}
        </MenuItem>
        {options.map((option, index) => (
          <MenuItem key={index} value={option.value ?? ''} disabled={option.disabled || false}>
            {option.icon && <Box marginRight={1}>{option.icon}</Box>}
            {option.label}
          </MenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
};
export default CustomDropdown;